import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Table,
  Input,
  Select,
  Tag,
  Button,
  Modal,
  message,
  Dropdown,
  Menu,
  Tooltip,
  Spin,
  Skeleton,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { RxRocket } from "react-icons/rx";
import {
  getActivationActions,
  deleteActivationAction,
  convertActivationActionToCampaign,
} from "../../api/Services";
import { formatRecommendationMessage } from "../../helpers/RecommendationMessageHelper";
import {
  hasPermission,
  privilegesEnum,
} from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  getTablePagination,
} from "../../helpers/TablePaginationHelper";
import styles from "./ActivationActions.module.scss";

const SKELETON_CELL_WIDTHS = ["90%", "75%", "45%", "55%", "60%", "40%", "24px"];

const ActivationActionsTableSkeleton = ({ rows }: { rows: number }) => (
  <div className={styles.tableSkeleton}>
    <div className={styles.tableSkeletonHeader}>
      {SKELETON_CELL_WIDTHS.map((width, index) => (
        <div key={`header-${index}`} className={styles.tableSkeletonCell}>
          <Skeleton.Input active size="small" style={{ width, height: 14 }} />
        </div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className={styles.tableSkeletonRow}>
        {SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
          <div
            key={`cell-${rowIndex}-${cellIndex}`}
            className={styles.tableSkeletonCell}
          >
            <Skeleton.Input
              active
              size="small"
              style={{
                width: cellIndex === 5 ? "72px" : width,
                height: cellIndex === 5 ? 22 : 16,
                borderRadius: cellIndex === 5 ? 12 : 4,
              }}
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const ActivationActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(
    parseInt(searchParams.get("page") || "1", 10) || 1,
  );
  const [pageSize, setPageSize] = useState(() => {
    const fromUrl = parseInt(searchParams.get("size") || "", 10);
    return fromUrl > 0 ? fromUrl : DEFAULT_TABLE_PAGE_SIZE;
  });
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState(
    searchParams.get("name") || "",
  );
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [statusFilterValue, setStatusFilterValue] = useState<
    string | undefined
  >(searchParams.get("status") || undefined);
  const [convertingId, setConvertingId] = useState<number | null>(null);

  const updateURL = useCallback(
    (filters: {
      name?: string;
      status?: string | undefined;
      page?: number;
      size?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters.name) params.set("name", filters.name);
      if (filters.status !== undefined) params.set("status", filters.status);
      if (filters.page && filters.page > 1)
        params.set("page", filters.page.toString());
      if (filters.size && filters.size !== DEFAULT_TABLE_PAGE_SIZE)
        params.set("size", filters.size.toString());
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const fetchActivationActions = useCallback(async () => {
    const requestId = beginRequest();
    setLoading(true);
    try {
      const response = await getActivationActions(actualPage, {
        name: debouncedNameFilterValue || undefined,
        status: statusFilterValue,
        size: pageSize,
      });

      if (isStaleRequest(requestId)) return;

      const totalElements = response.totalElements ?? 0;
      const totalPages = Math.max(1, Math.ceil(totalElements / pageSize) || 1);

      if (totalElements > 0 && actualPage > totalPages) {
        setActualPage(totalPages);
        updateURL({
          name: nameFilterValue,
          status: statusFilterValue,
          page: totalPages,
          size: pageSize,
        });
        return;
      }

      setData(response.activationActions ?? []);
      setTotalSize(totalElements);
    } catch (e) {
      if (isStaleRequest(requestId)) return;
      message.error(t("Common-ErrorMessage"));
      setData([]);
    } finally {
      if (!isStaleRequest(requestId)) {
        setLoading(false);
      }
    }
  }, [
    actualPage,
    pageSize,
    debouncedNameFilterValue,
    statusFilterValue,
    nameFilterValue,
    beginRequest,
    isStaleRequest,
    updateURL,
    t,
  ]);

  useEffect(() => {
    fetchActivationActions();
  }, [fetchActivationActions]);

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("ActivationAction-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteActivationAction(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchActivationActions();
  };

  const handleConvertToCampaign = async (id: number) => {
    setConvertingId(id);
    try {
      const result = await convertActivationActionToCampaign(id);
      if (result.success && result.data?.campaignId) {
        message.success(t("ActivationAction-ConvertSuccess"));
        navigate(
          `/commercial-intelligence/campaigns/${result.data.campaignId}/view`,
        );
        return;
      }
      if (result.timedOut) {
        message.warning(
          t(
            "ActivationAction-ConvertTimeout",
            "La conversión está tardando. Actualizá la lista: si aparece Aprobada, la campaña ya se creó.",
          ),
        );
        await fetchActivationActions();
        return;
      }
      message.error(result.message || t("Common-ErrorMessage"));
      await fetchActivationActions();
    } finally {
      setConvertingId(null);
    }
  };

  const handlePagination = (page: number, nextPageSize?: number) => {
    const size = nextPageSize && nextPageSize > 0 ? nextPageSize : pageSize;
    const pageChangedSize = size !== pageSize;
    const nextPage = pageChangedSize ? 1 : page;
    setPageSize(size);
    setActualPage(nextPage);
    updateURL({
      name: nameFilterValue,
      status: statusFilterValue,
      page: nextPage,
      size,
    });
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setStatusFilterValue(undefined);
    setActualPage(1);
    updateURL({ page: 1, size: pageSize });
  };

  const hasActiveFilters = () => {
    return nameFilterValue !== "" || statusFilterValue !== undefined;
  };

  const hasNoData = () => {
    return !loading && data.length === 0 && totalSize === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="ActivationAction" />;
    }
    if (hasNoDataAtAll()) {
      return <EmptyState type="noData" entityName="ActivationAction" />;
    }
    return null;
  };

  const formatAudience = (count: number) => {
    if (count == null) return "-";
    return count.toLocaleString("es-AR");
  };

  const columns = [
    {
      title: t("ActivationAction-Action"),
      dataIndex: "name",
      render: (name: string, record: any) =>
        formatRecommendationMessage(t, record.nameKey, record.nameParams, name),
    },
    {
      title: t("ActivationAction-SourceSegment"),
      dataIndex: "sourceSegmentName",
      render: (name: string, record: any) => {
        const resolved = formatRecommendationMessage(
          t,
          record.sourceSegmentNameKey,
          record.sourceSegmentNameParams,
          name
        );
        if (!resolved && !record.sourceSegmentId) return "-";
        if (record.sourceSegmentId) {
          return (
            <a
              href={`/commercial-intelligence/segments/${record.sourceSegmentId}/view`}
              className={styles.nameLink}
            >
              {resolved || record.sourceSegmentId}
            </a>
          );
        }
        return resolved || "-";
      },
    },
    {
      title: t("ActivationAction-Audience"),
      dataIndex: "audienceCount",
      render: (count: number) => formatAudience(count),
    },
    {
      title: t("ActivationAction-EstimatedConversion"),
      dataIndex: "estimatedConversion",
      render: (count: number) => formatAudience(count),
    },
    {
      title: t("Common-EstimatedImpact"),
      dataIndex: "estimatedImpact",
      render: (value: number) =>
        value === null || value === undefined ? "-" : `$${value}`,
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      render: (status: string) => (
        <Tag color={status === "APPROVED" ? "green" : "blue"}>
          {status === "APPROVED"
            ? t("ActivationAction-Status-Approved")
            : t("ActivationAction-Status-Suggested")}
        </Tag>
      ),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (_text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(
                    `/commercial-intelligence/opportunities/activation-actions/${record.id}/view`,
                  )
                }
              >
                {t("ActivationAction-ViewDetail")}
              </Menu.Item>
              <Menu.Item
                key="createCampaign"
                icon={<NotificationOutlined />}
                onClick={() => handleConvertToCampaign(record.id)}
                disabled={record.campaignId != null}
              >
                {t("ActivationAction-ConvertToCampaign")}
              </Menu.Item>
              {hasPermission(privilegesEnum.DELETE_ACTIVATION_ACTIONS) &&
                record.campaignId == null && (
                  <Menu.Item
                    key="delete"
                    icon={<DeleteOutlined />}
                    onClick={() => showDeleteConfirm(record.id)}
                  >
                    {t("Common-Delete")}
                  </Menu.Item>
                )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("ActivationAction-Title")}
        icon={<RxRocket />}
        showCreateButton={false}
      />
      <div className={styles.contentWrapper}>
        <Spin
          className={styles.convertSpin}
          spinning={convertingId !== null}
          tip={t("ActivationAction-ConvertingToCampaign")}
          size="large"
        >
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-SearchByName")}
                    className={styles.filterInput}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => {
                      setNameFilterValue(e.target.value);
                      setActualPage(1);
                      updateURL({
                        name: e.target.value,
                        status: statusFilterValue,
                        page: 1,
                        size: pageSize,
                      });
                    }}
                    value={nameFilterValue}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    showSearch
                    allowClear
                    value={statusFilterValue}
                    className={styles.filterSelect}
                    placeholder={t("Common-SearchByState")}
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onSelect={(_: any, option: any) => {
                      if (option) {
                        setStatusFilterValue(option.value);
                        setActualPage(1);
                        updateURL({
                          name: nameFilterValue,
                          status: option.value,
                          page: 1,
                          size: pageSize,
                        });
                      }
                    }}
                    onClear={() => {
                      setStatusFilterValue(undefined);
                      setActualPage(1);
                      updateURL({
                        name: nameFilterValue,
                        status: undefined,
                        page: 1,
                        size: pageSize,
                      });
                    }}
                  >
                    <Select.Option value="SUGGESTED">
                      {t("ActivationAction-Status-Suggested")}
                    </Select.Option>
                    <Select.Option value="APPROVED">
                      {t("ActivationAction-Status-Approved")}
                    </Select.Option>
                  </Select>
                </div>
                <div className={styles.filterClearActions}>
                  <Button
                    onClick={handleClearFilters}
                    className={styles.clearButton}
                    icon={<ClearOutlined />}
                    disabled={!hasActiveFilters()}
                  >
                    {t("Common-Clear")}
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <ActivationActionsTableSkeleton
                rows={Math.min(pageSize, DEFAULT_TABLE_PAGE_SIZE)}
              />
            ) : (
              <Table
                size="small"
                className={styles.table}
                rowKey={(record) => record.id}
                columns={columns}
                dataSource={data}
                scroll={{ x: 1100 }}
                pagination={getTablePagination(t, {
                  current: actualPage,
                  total: totalSize,
                  pageSize,
                  showSizeChanger: true,
                  onChange: handlePagination,
                })}
                locale={{
                  emptyText: getEmptyState(),
                }}
              />
            )}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default ActivationActions;
